'use client';

import type { Header as HeaderType } from '@dobrozem/cms';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import logo from '@/assets/icons/logo.svg';
import { EnterIcon } from '@/assets/react-icons/EnterIcon';
import { ExitIcon } from '@/assets/react-icons/ExitIcon';
import { HeartIcon } from '@/assets/react-icons/HeartIcon';
import { PhoneIcon } from '@/assets/react-icons/PhoneIcon';
import { UserIcon } from '@/assets/react-icons/UserIcon';
import { Button } from '@/components/Button';
import { CMSLink } from '@/components/CMSLink';
import { Container } from '@/components/Container';
import { LogoutModal } from '@/components/LogoutModal';
import { Modal } from '@/components/Modal';
import { Responsive } from '@/components/Responsive';
import { Text } from '@/components/Text';
import { useUser } from '@/contexts/AuthProvider';
import { useGlobals } from '@/contexts/GlobalsContext';
import { useDisclosure } from '@/hooks/useDisclosure';
import { Calculator } from './Calculator';
import styles from './Header.module.scss';
type BtnMenuProps = {
  isAuth: boolean;
  isOpen: boolean;
  menu: HeaderType['menu'];
  onOpenLogout: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCalculator: React.Dispatch<React.SetStateAction<boolean>>;
};
const EnterExit = ({
  isAuth,
  onOpenLogout,
  setOpen
}: {
  isAuth: boolean;
  onOpenLogout: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isAuth) {
      onOpenLogout(event);
    }
  };
  return <div className={styles['enterExit-wrapper']} onClick={handleClick} data-sentry-component="EnterExit" data-sentry-source-file="Header.tsx">
      {isAuth ? <ExitIcon /> : <EnterIcon />}

      <Text type='button_small' data-sentry-element="Text" data-sentry-source-file="Header.tsx">
        {isAuth ? 'Вийти' : <Link href='/auth/sign-in' onClick={() => setOpen(false)}>
            Увійти
          </Link>}
      </Text>
    </div>;
};
function stringsMatch(str1: string, str2: string): boolean {
  const parts1 = str1.split('/');
  const parts2 = str2.split('/');
  for (const part1 of parts1) {
    if (parts2.includes(part1) && part1 !== '') {
      return true;
    }
  }
  return false;
}
const Menu = ({
  isModal,
  menu,
  setOpenCalculator,
  setOpenMenu
}: {
  isModal: boolean;
  menu: HeaderType['menu'];
  setOpenCalculator: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMenu?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const pathname = usePathname();
  return <nav className={clsx(styles['menu-wrapper'], isModal && styles.isModal)} data-sentry-component="Menu" data-sentry-source-file="Header.tsx">
      {menu?.map(({
      id,
      link
    }) => link.label !== 'Калькулятор' ? <CMSLink className={clsx(styles['menu-item'], stringsMatch(link.url as string, pathname) && styles.current)} key={id} link={link} onClick={() => setOpenMenu && setOpenMenu(false)}>
            <Text color={'black'} type={isModal ? 'h3' : 'button_small'}>
              {link.label}
            </Text>
          </CMSLink> : <div className={styles['menu-item']} key={id} onClick={() => setOpenCalculator(true)}>
            <Text type={isModal ? 'h3' : 'button_small'}>{link.label}</Text>
          </div>)}
    </nav>;
};
const UserActions = ({
  fullDescription,
  setOpen
}: {
  fullDescription?: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    setShowContactSupportModal
  } = useGlobals();
  return <div className={clsx(styles['userActions-wrapper'], fullDescription && styles.isFull)} data-sentry-component="UserActions" data-sentry-source-file="Header.tsx">
      <Link className={clsx(styles['user-action'], fullDescription && styles.isFull)} href={'/account/settings'} onClick={() => setOpen(false)} data-sentry-element="Link" data-sentry-source-file="Header.tsx">
        <UserIcon data-sentry-element="UserIcon" data-sentry-source-file="Header.tsx" />
        {fullDescription && <Text type='p2'>Мій профіль</Text>}
      </Link>
      <Link className={clsx(styles['user-action'], fullDescription && styles.isFull)} href={'/account/favorites'} onClick={() => setOpen(false)} data-sentry-element="Link" data-sentry-source-file="Header.tsx">
        <HeartIcon data-sentry-element="HeartIcon" data-sentry-source-file="Header.tsx" />
        {fullDescription && <Text type='p2'>Обране</Text>}
      </Link>
      <div className={clsx(styles['user-action'], fullDescription && styles.isFull)} onClick={() => setShowContactSupportModal(true)}>
        <PhoneIcon data-sentry-element="PhoneIcon" data-sentry-source-file="Header.tsx" />
        {fullDescription && <Text type='p2'>Замовити консультацію</Text>}
      </div>
    </div>;
};
const BtnMenu = ({
  isAuth,
  isOpen,
  menu,
  onOpenLogout,
  setOpen,
  setOpenCalculator
}: BtnMenuProps) => {
  const createOfferButton = <>
      {!isAuth ? <Link href='/auth/sign-in' onClick={() => setOpen(false)}>
          <Button asDiv bordered small>
            <Text>Додати ділянку</Text>
          </Button>
        </Link> : <Link href='/offer/create' onClick={() => setOpen(false)}>
          <Button asDiv bordered small>
            <Text>Додати ділянку</Text>
          </Button>
        </Link>}
    </>;
  return <Modal animationType='fromRight' centeredContent={false} className={styles.modal} contentClassName={styles.modalContent} onOpenChange={setOpen} open={isOpen} trigger={<div className={styles['btn-menu']}>
          <svg fill='none' height='48' viewBox='0 0 48 48' width='48' xmlns='http://www.w3.org/2000/svg'>
            <path clipRule='evenodd' d='M42 14H6V10H42V14ZM42 26H6V22H42V26ZM6 38H28V34H6V38Z' fill='black' fillRule='evenodd' />
          </svg>
        </div>} data-sentry-element="Modal" data-sentry-component="BtnMenu" data-sentry-source-file="Header.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="Header.tsx">
        <div className={styles['content-wrapper']}>
          <div className={styles['left']}>
            <Link className={styles['logo']} href={'/'} data-sentry-element="Link" data-sentry-source-file="Header.tsx">
              <Image alt='logo' src={logo} data-sentry-element="Image" data-sentry-source-file="Header.tsx" />
            </Link>
            <Responsive showFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">{isAuth && <UserActions setOpen={setOpen} />}</Responsive>
          </div>
          <div className={styles['right']}>
            <EnterExit isAuth={isAuth} onOpenLogout={onOpenLogout} setOpen={setOpen} data-sentry-element="EnterExit" data-sentry-source-file="Header.tsx" />
            <Responsive showFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">{createOfferButton}</Responsive>
            <div className={styles['btn-close']} onClick={() => setOpen(false)}>
              <svg fill='none' height='48' viewBox='0 0 48 48' width='48' xmlns='http://www.w3.org/2000/svg' data-sentry-element="svg" data-sentry-source-file="Header.tsx">
                <path clipRule='evenodd' d='M26.8308 24.0003L39.415 11.4162C40.1968 10.635 40.1968 9.36755 39.415 8.58638C38.6331 7.80454 37.367 7.80454 36.5851 8.58638L24.001 21.1705L11.4162 8.58638C10.6344 7.80454 9.36822 7.80454 8.58638 8.58638C7.80454 9.36755 7.80454 10.635 8.58638 11.4162L21.1712 24.0003L8.58638 36.5845C7.80454 37.3656 7.80454 38.6331 8.58638 39.4143C8.9773 39.8045 9.48963 40 10.0013 40C10.513 40 11.0253 39.8045 11.4162 39.4136L24.001 26.8295L36.5851 39.4136C36.976 39.8045 37.4884 40 38 40C38.5117 40 39.024 39.8045 39.415 39.4136C40.1968 38.6324 40.1968 37.365 39.415 36.5838L26.8308 24.0003Z' fill='black' fillRule='evenodd' data-sentry-element="path" data-sentry-source-file="Header.tsx" />
              </svg>
            </div>
          </div>
        </div>
        <div className={styles['menu-modal-wrapper']}>
          <div className={styles['menu-modal']}>
            <Menu isModal menu={menu} setOpenCalculator={setOpenCalculator} setOpenMenu={setOpen} data-sentry-element="Menu" data-sentry-source-file="Header.tsx" />
          </div>
          {isAuth && <UserActions fullDescription setOpen={setOpen} />}
          <Responsive className={styles['btn-modal-wrapper']} showOnly={'mobile'} data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">
            {createOfferButton}
          </Responsive>
        </div>
      </Container>
    </Modal>;
};
export const Header = () => {
  const [isOpen, setOpen] = useState(false);
  const [isOpenCalculator, setOpenCalculator] = useState(false);
  const {
    isOpen: isOpenLogout,
    onClose: onCloseLogout,
    onOpen: onOpenLogout
  } = useDisclosure();
  const {
    header
  } = useGlobals();
  const user = useUser();
  const isAuth = !!user;
  const createOfferButton = <>
      {!isAuth ? <Link href='/auth/sign-in'>
          <Button asDiv bordered small>
            <Text>Додати ділянку</Text>
          </Button>
        </Link> : <Link href='/offer/create'>
          <Button asDiv bordered small>
            <Text>Додати ділянку</Text>
          </Button>
        </Link>}
    </>;
  return <>
      <header className={styles.header}>
        <Container data-sentry-element="Container" data-sentry-source-file="Header.tsx">
          <div className={styles['content-wrapper']}>
            <div className={styles['left']}>
              <Link className={styles['logo']} href={'/'} data-sentry-element="Link" data-sentry-source-file="Header.tsx">
                <Image alt='logo' src={logo} data-sentry-element="Image" data-sentry-source-file="Header.tsx" />
              </Link>
              <Responsive showFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">
                <Menu isModal={false} menu={header.menu} setOpenCalculator={setOpenCalculator} data-sentry-element="Menu" data-sentry-source-file="Header.tsx" />
              </Responsive>
              <Responsive showFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">
                {isAuth && <UserActions setOpen={setOpen} />}
              </Responsive>
            </div>
            <div className={styles['right']}>
              <EnterExit isAuth={isAuth} onOpenLogout={onOpenLogout} setOpen={setOpen} data-sentry-element="EnterExit" data-sentry-source-file="Header.tsx" />
              <Responsive showFrom='tablet' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">{createOfferButton}</Responsive>
              <Responsive hideFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="Header.tsx">
                <BtnMenu isAuth={isAuth} isOpen={isOpen} menu={header.menu} onOpenLogout={onOpenLogout} setOpen={setOpen} setOpenCalculator={setOpenCalculator} data-sentry-element="BtnMenu" data-sentry-source-file="Header.tsx" />
              </Responsive>
            </div>
          </div>
        </Container>
      </header>
      <LogoutModal onCloseLogout={onCloseLogout} open={isOpenLogout} data-sentry-element="LogoutModal" data-sentry-source-file="Header.tsx" />
      <Modal className={styles['modal-content-calculator']} contentClassName={styles.contentModal} onOpenChange={setOpenCalculator} open={isOpenCalculator} data-sentry-element="Modal" data-sentry-source-file="Header.tsx">
        <Calculator setOpenCalculator={setOpenCalculator} data-sentry-element="Calculator" data-sentry-source-file="Header.tsx" />
      </Modal>
    </>;
};